import { AsyncPipe, CommonModule } from "@angular/common";
import { Component, inject, ViewChild } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatChipsModule } from "@angular/material/chips";
import { provideNativeDateAdapter } from "@angular/material/core";
import {
  MatDatepicker,
  MatDatepickerModule,
} from "@angular/material/datepicker";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTabsModule } from "@angular/material/tabs";
import { MatToolbarModule } from "@angular/material/toolbar";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { select, Store } from "@ngrx/store";
import { EditorModule } from "@tinymce/tinymce-angular";
import { Editor, NgxEditorModule, Toolbar } from "ngx-editor";
import { firstValueFrom, Observable, take } from "rxjs";
import { AppState } from "../../../app.state";
import { ConfirmDialogComponent } from "../../../confirm-dialog/confirm-dialog.component";
import { ICMoverDespacho } from "../../../interfaces/catalogos.";
import { LoadingService } from "../../../loading/loading.service";
import { OrderByPipe } from "../../../pipes/order-by.pipe";
import { AcuerdosService } from "../../../services/acuerdos/acuerdos.service";
import { DespachoService } from "../../../services/despacho/despacho.service";
import { selectCatalogosMoverMrb } from "../../../states/catalogos/selector";
import {
  getEntradasByIdEmpresa,
  resetEntradasMoverMrb,
} from "../../../states/despacho/actions";
import { selectDespachoEntradas } from "../../../states/despacho/selector";
import { IEntrada } from "../../../interfaces/entradas";
import { IEntradaDespacho } from "../../../interfaces/despacho";
import { MatTooltipModule } from "@angular/material/tooltip";
import { IMoverAcuerdo } from "../../../interfaces/acuerdo";
import { moverTemaMrb } from "../../../states/acuerdos-mrb/actions";
import { EntregablesService } from "../../../services/entregables/entregables.service";
import { AcuerdosUtilsService } from "../utils";
import { StorageService } from "../../../services/storage/files.service";

@Component({
  selector: "app-mover-despacho",
  standalone: true,
  imports: [
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    FormsModule,
    MatRadioModule,
    MatDatepickerModule,
    MatSelectModule,
    MatAutocompleteModule,
    AsyncPipe,
    MatChipsModule,
    EditorModule,
    CommonModule,
    NgxEditorModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatCardModule,
    OrderByPipe,
    MatTooltipModule,
    RouterLink,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: "./mover-acuerdo.component.html",
  styleUrl: "./mover-acuerdo.component.css",
})
export class MoverAcuerdoComponent {
  @ViewChild("picker") picker!: MatDatepicker<Date>;
  private store: Store<AppState> = inject(Store);

  public formAcuerdo!: FormGroup;
  private formBuilder: FormBuilder = inject(FormBuilder);

  minDate!: Date;
  catalogos$!: Observable<ICMoverDespacho>;
  public entradas$!: Observable<any>;
  public listaEntradas: IEntrada[] = [];
  private stateEntradas: any = this.store.pipe(select(selectDespachoEntradas));
  public temaAMover: any = [];
  public mostrarEntradas = false;
  constructor(
    public dialog: MatDialog,
    private acuerdosService: AcuerdosService,
    private despachoService: DespachoService,
    private router: ActivatedRoute,
    private route: Router,
    private loadingService: LoadingService,
    private entregablesService: EntregablesService,
    private acuerdosUtils: AcuerdosUtilsService,
    private storageService: StorageService
  ) {}

  ngOnInit() {
    this.initForm();
    this.catalogos$ = this.store.select(selectCatalogosMoverMrb);
    this.entradas$ = this.store.select(selectDespachoEntradas).pipe();
    this.entradas$.subscribe((entradas) => {
      this.listaEntradas = entradas;
    });
  }

  initForm() {
    this.store.dispatch(resetEntradasMoverMrb());
    this.formAcuerdo = this.formBuilder.group({
      descripcionTema_html: ["", [Validators.required]],
      tipo_forma_envio: ["", [Validators.required]],
      nombreTema: [{ value: "", disabled: true }, [Validators.required]],
      empresa: [{}, Validators.required],
      id_tipo_despacho: ["", Validators.required],
      entradaSelected: [""],
      entradas: [[], Validators.required],
      banEsImpreso: [false, Validators.required],
    });
    const id_tema = this.router.snapshot.paramMap.get("id_tema") ?? "";
    if (!id_tema) {
      return;
    }

    this.despachoService
      .consultarTemaAMover(id_tema, 1)
      .pipe(take(1))
      .subscribe((tema) => {
        this.temaAMover = tema;
        this.formAcuerdo.get("entradas")?.setValue(this.temaAMover.entradas);
        this.formAcuerdo
          .get("nombreTema")
          ?.setValue(this.temaAMover.nombreTema);
        this.formAcuerdo
          .get("descripcionTema_html")
          ?.setValue(this.temaAMover.descripcionTema_html);
      });
  }

  stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  showModalError(title: string, message: string, width?: number) {
    const px = width ? width : 350;
    this.dialog.open(ConfirmDialogComponent, {
      width: px.toString() + "px",
      data: {
        title,
        message,
        showButtons: {
          accept: true,
          cancel: false,
        },
        direction: "end",
        buttonsName: { cancel: "CANCELAR", accept: "ACEPTAR" },
      },
    });
  }

  seleccionarEmpresa(empresa: any) {
    const entradaSelected = this.formAcuerdo.get(
      "entradaSelected"
    ) as FormControl;

    entradaSelected.setValue("");
    this.store.dispatch(getEntradasByIdEmpresa({ idEmpresa: empresa.id }));
  }

  async onInput(event: any, temaForm: any) {
    const initialState = await this.stateEntradas.state().formaEnvio.entradas;
    console.log("initialState", initialState);

    const entradasActuales = temaForm.get("entradas").value;
    const entradasFiltradas: IEntrada[] = this.filtrarEntradasActuales(
      entradasActuales,
      initialState
    );

    const nombreEntrada = [undefined, null, ""].includes(
      event.target.value.trim()
    )
      ? ""
      : event.target.value.trim();

    if (!nombreEntrada) {
      this.listaEntradas = entradasFiltradas;
    }
    if (nombreEntrada) {
      this.listaEntradas = entradasFiltradas.filter((entrada: IEntrada) => {
        return (
          entrada.nombreEntradaCompleto &&
          entrada.nombreEntradaCompleto
            .toLowerCase()
            .includes(nombreEntrada.toLowerCase())
        );
      });
    }
  }

  filtrarEntradasActuales(
    entradaControl: IEntrada[],
    orignalState: IEntrada[]
  ) {
    return orignalState.filter((entrada: IEntrada) => {
      return !entradaControl.some(
        (controlEntrada: IEntrada) =>
          controlEntrada.id_entrada === entrada.id_entrada
      );
    });
  }

  puedeEliminar(entrada: any) {
    return this.acuerdosUtils.getPermisoEscritura(entrada.id_categorizacion);
  }

  selectEntrada(entrada: any, listaEntradas: any) {
    this.loadingService.show()
    const entradaSelected = this.formAcuerdo.get(
      "entradaSelected"
    ) as FormControl;

    entradaSelected.setValue("");

    const entradas = this.formAcuerdo.get("entradas") as FormControl;

    const dataNuevaEntrada: IEntradaDespacho = {
      id_entrada: entrada.id_entrada,
      id_categorizacion: entrada.id_categorizacion,
      id_tema: null,
      indice: null,
      nombreEntrada: entrada.nombreEntrada,
      nombreEntradaCompleto: entrada.nombreEntradaCompleto,
      banConSeguimiento: entrada.banConSeguimiento,
      justificacionSeguimiento: entrada.justificacionSeguimiento,
      banEsActivo: entrada.banEsActivo,
      id_empresa: entrada.id_empresa,
      desc_empresa: entrada.desc_empresa,
      fecha_inicio: entrada.fecha_inicio,
      remitente_descripcion: entrada.remitente_descripcion,
      remitenteCompleto: entrada.remitenteCompleto,
      idSobre: entrada.idSobre,
      documentoSoporte: entrada.documentoSoporte,
      nombre_empresa: entrada.desc_empresa,
      value: entrada.id_entrada,
      entregables: [],
      archivos: [],
      esFront: false,
      puedeVer: false,
      nuevaEntrada: true,
    };

    this.acuerdosService
      .getEntregableArchivos(entrada.id_entrada)
      .then((data) => {
        data.subscribe((files) => {
          console.log("files", files);

          dataNuevaEntrada.archivos = files.archivos;
          dataNuevaEntrada.entregables = files.entregables;
          console.log("dataNuevaEntrada", dataNuevaEntrada);
          const nuevasEntradas = [...entradas.value, dataNuevaEntrada];
          entradas.setValue(nuevasEntradas);

           this.listaEntradas = this.listaEntradas.filter(
             (ent) => ent.id_entrada == entrada.id_entrada
           );
               this.loadingService.hide();
        });
      });
  }

  verArchivo(entrada: any, archivo: any) {
    console.log("entrada", entrada);
    console.log("archivo", archivo);

    this.storageService
      .obtenerArchivoUrl(entrada.id_entrada, archivo.nombre_archivo)
      .subscribe((data) => {
        console.log("data", data);
        if (!data.error) {
          window.open(data.url, "_blank");
        }
      });
  }

  descargaEntregable(entrada: string, entregable: string) {
    this.loadingService.show();
    this.entregablesService
      .descargarEntregable({ entrada, entregable })
      .subscribe((data) => {
        console.log("data", data);
        if (!data.error && !data.data.error) {
          var link = document.createElement("a");
          link.href = data.data;
          link.setAttribute("id", "descargaEntregable");
          link.download = "descargaEntregable";
          link.click();
          window.URL.revokeObjectURL(link.href);

          setTimeout(() => {
            const linkElement = document.getElementById("descargaEntregable");
            if (linkElement) {
              document.body.removeChild(linkElement);
            }
          }, 2000);
          this.loadingService.hide();
        } else {
          const message = data.data.message;
          console.log("message", message);
          this.loadingService.hide();
          this.showModalError("Error", message.mensaje, 550);
        }
      });
  }

  removeEntrada(entrada: any) {}

  disable() {
    const tipo_forma_envio = this.formAcuerdo.get(
      "tipo_forma_envio"
    ) as FormControl;

    tipo_forma_envio.disable();
  }

  cancelar() {
    this.initForm();
  }

  isFormValid(): boolean {
    return Object.values(this.formAcuerdo.controls).every(
      (control) => control.disabled || control.valid
    );
  }
  eliminarEntrada(entrada: any) {
    console.log("eliminarEntrada", entrada);

    const entradas = this.formAcuerdo.get("entradas") as FormControl;
    const filtro = entradas.value.filter(
      (ent: any) => ent.id_entrada !== entrada.id_entrada
    );
    entradas.setValue(filtro);
    this.listaEntradas = [...this.listaEntradas, entrada];
  }
  async guardar() {
    this.loadingService.show();
    const data = this.formAcuerdo.getRawValue();
    const id_tema = this.router.snapshot.paramMap.get("id_tema") ?? "";
    const payload: IMoverAcuerdo = {
      banEsImpreso: data.banEsImpreso,
      id_tema,
      nombreTema: data.nombreTema,
      descripcionTema: data.nombreTema,
      descripcionTema_html: data.descripcionTema_html,
      entradas: data.entradas,
      id_empresa: data.empresa.id,
      nombre_empresa: data.empresa.descripcion,
      id_tipo_despacho: data.id_tipo_despacho,
    };

    try {
      const moverTema = await this.acuerdosService.moverTemaAcuerdoMrBDespacho(
        payload
      );
      if (!moverTema.error) {
        this.loadingService.hide();
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
          width: "450px",
          data: {
            title: "Éxito",
            message: "Se movió correctamente el tema",
            showButtons: {
              accept: true,
              cancel: false,
            },
            direction: "end",
            buttonsName: { cancel: "CANCELAR", accept: "ACEPTAR" },
          },
          disableClose:true
        });

        dialogRef.componentInstance.confirmAction.subscribe(() => {
          this.route.navigate([`acuerdos-mrb-listado`]);
        });
      }
    } catch (error) {
      this.loadingService.hide();
    }

    //this.store.dispatch(moverTemaMrb({ payload }));

    console.log("payload", payload);
  }

  onFocus(temaForm: any) {
    this.mostrarEntradas = true;
    this.setEntradasOnFocusOnBlur(temaForm);
  }

  onBlur(temaForm: any) {
    setTimeout(() => {
      this.mostrarEntradas = false;
      this.setEntradasOnFocusOnBlur(temaForm);
    }, 200);
  }

  async setEntradasOnFocusOnBlur(temaForm: any) {
  
        const initialState = await this.stateEntradas.state().formaEnvio
          .entradas;
    const entradas = temaForm.get("entradas").value;
    const entradasActuales = entradas;
    const entradasFiltradas: IEntrada[] = this.filtrarEntradasActuales(
      entradasActuales,
      initialState
    );

    this.listaEntradas = entradasFiltradas;
  }
}
